import type { Block, PresetRef } from "../contract/types";
import { normalizeBlock, normalizePreset, type SessionMessage } from "../api/client";

/**
 * Reducer del stream. Es puro: recibe eventos ya parseados y devuelve el
 * estado siguiente. `useRun` solo se encarga del transporte.
 *
 * El estado acumula, por turno: texto en curso, bloques emitidos, trazas de
 * tools y la skill cargada. El backend mantiene los `id` de bloque estables
 * durante la sesión, así que se usan como clave de deduplicación.
 */

export type RunStatus = "idle" | "connecting" | "streaming" | "done" | "error";

export interface StreamError {
  code: string;
  message: string;
  fatal: boolean;
}

export interface ToolTrace {
  id: string;
  name: string;
  argsPreview: string;
  ok: boolean | null;
  preview: string;
}

export interface RunUsage {
  inputTokens: number;
  outputTokens: number;
}

export interface Turn {
  id: string;
  role: "user" | "assistant";
  createdAt: string;
  /** La respuesta: lo único que el estudiante lee de entrada. */
  text: string;
  /**
   * Lo que el agente dijo mientras llamaba a herramientas. No se pierde ni se
   * mezcla con la respuesta: se enseña tras un desplegable, para quien quiera
   * seguir el razonamiento.
   */
  processText: string;
  blocks: Block[];
  tools: ToolTrace[];
  skills: string[];
  status: RunStatus;
  error: StreamError | null;
  usage: RunUsage | null;
  iterations: number | null;
  runId: string | null;
}

export interface StreamState {
  turns: Turn[];
  /** Turno del asistente que se está construyendo ahora mismo. */
  activeTurnId: string | null;
  activeRunId: string | null;
  status: RunStatus;
  preset: PresetRef | null;
  lastEventAt: number | null;
}

export const initialStreamState: StreamState = {
  turns: [],
  activeTurnId: null,
  activeRunId: null,
  status: "idle",
  preset: null,
  lastEventAt: null,
};

/* -------------------------------------------------------------------------- */
/* Eventos del protocolo                                                      */
/* -------------------------------------------------------------------------- */

export interface RunStartData {
  run_id: string;
  session_id: string;
  message_id: string;
}

export interface RunDoneData {
  usage?: { input_tokens?: number; output_tokens?: number };
  iterations?: number;
}

export type StreamEvent =
  | { kind: "run.start"; data: RunStartData }
  | { kind: "text.delta"; data: { delta: string; phase: "answer" | "process" } }
  | { kind: "block.add"; data: { block: Block } }
  | { kind: "tool.call"; data: { id: string; name: string; args_preview: string } }
  | { kind: "tool.result"; data: { id: string; ok: boolean; preview: string } }
  | { kind: "skill.loaded"; data: { name: string } }
  | { kind: "run.done"; data: RunDoneData }
  | { kind: "error"; data: { code: string; message: string } }
  | { kind: "ignore"; name: string };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function readString(source: Record<string, unknown>, key: string, fallback = ""): string {
  const value = source[key];
  return typeof value === "string" ? value : fallback;
}

/**
 * Convierte un evento SSE crudo en un evento tipado. Los eventos que no
 * reconocemos no rompen el stream: se devuelven como `ignore` para que el
 * reducer los anote y la UI siga viva.
 */
export function parseStreamEvent(name: string, payload: unknown): StreamEvent {
  const data = isRecord(payload) ? payload : {};

  switch (name) {
    case "run.start":
      return {
        kind: "run.start",
        data: {
          run_id: readString(data, "run_id"),
          session_id: readString(data, "session_id"),
          message_id: readString(data, "message_id"),
        },
      };
    case "text.delta":
      return {
        kind: "text.delta",
        data: {
          delta: readString(data, "delta"),
          phase: data.phase === "process" ? "process" : "answer",
        },
      };
    case "block.add": {
      // El backend emite el preset como un `block.add` normal, con el
      // `PresetRef` dentro (`{type: "preset", preset: {name, data}}`). Se
      // tolera además la forma suelta por si algún día llega sin envolver.
      const preset = normalizePreset(data.block) ?? normalizePreset(data.preset);
      if (preset && !(isRecord(data.block) && data.block.type === "preset")) {
        return {
          kind: "block.add",
          data: {
            block: {
              id: readString(isRecord(data.block) ? data.block : data, "id", `blk_preset_${Date.now()}`),
              type: "preset",
              preset,
            } as Block,
          },
        };
      }
      const block = normalizeBlock(data.block);
      if (!block) return { kind: "ignore", name };
      return { kind: "block.add", data: { block } };
    }
    case "tool.call":
      return {
        kind: "tool.call",
        data: {
          id: readString(data, "id", `tool_${Math.random().toString(36).slice(2, 8)}`),
          name: readString(data, "name", "tool"),
          args_preview: readString(data, "args_preview"),
        },
      };
    case "tool.result":
      return {
        kind: "tool.result",
        data: {
          id: readString(data, "id"),
          ok: data.ok === true,
          preview: readString(data, "preview"),
        },
      };
    case "skill.loaded":
      return { kind: "skill.loaded", data: { name: readString(data, "name") } };
    case "run.done":
      return { kind: "run.done", data: data };
    case "error":
      return {
        kind: "error",
        data: {
          code: readString(data, "code", "stream_error"),
          message: readString(data, "message", "El stream terminó con un error."),
        },
      };
    default:
      return { kind: "ignore", name };
  }
}

/* -------------------------------------------------------------------------- */
/* Acciones                                                                   */
/* -------------------------------------------------------------------------- */

export type StreamAction =
  | { type: "session/loaded"; messages: SessionMessage[] }
  | { type: "user/append"; id: string; content: string; createdAt: string }
  | { type: "run/prepare"; runId: string; messageId: string; createdAt: string }
  | { type: "run/failed"; message: string; createdAt: string }
  | { type: "run/replay"; runId: string }
  | { type: "stream/event"; event: StreamEvent }
  | { type: "stream/interrupted"; message: string }
  | { type: "stream/stopped" }
  | { type: "preset/open"; preset: PresetRef }
  | { type: "preset/close" }
  | { type: "state/reset" };

/* -------------------------------------------------------------------------- */
/* Reducer                                                                    */
/* -------------------------------------------------------------------------- */

function localTurnId(): string {
  return `turn_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
}

function updateTurn(
  state: StreamState,
  turnId: string | null,
  updater: (turn: Turn) => Turn,
): StreamState {
  if (!turnId) return state;
  let touched = false;
  const turns = state.turns.map((turn) => {
    if (turn.id !== turnId) return turn;
    touched = true;
    return updater(turn);
  });
  return touched ? { ...state, turns } : state;
}

function appendBlock(blocks: Block[], block: Block): Block[] {
  // Un bloque reemitido (reconexión) se reemplaza en su sitio, no se duplica.
  const index = blocks.findIndex((existing) => existing.id === block.id);
  if (index === -1) return [...blocks, block];
  const next = blocks.slice();
  next[index] = block;
  return next;
}

export function streamReducer(state: StreamState, action: StreamAction): StreamState {
  switch (action.type) {
    case "state/reset":
      return initialStreamState;

    case "session/loaded": {
      const turns: Turn[] = [];
      for (const message of action.messages) {
        if (message.role === "system") continue;
        // Las partes se guardan en orden cronológico, pero aquí se separan por
        // fase: el razonamiento y la respuesta viven en sitios distintos de la
        // UI, así que el orden de llegada deja de importar.
        const textOf = (phase: "answer" | "process") =>
          message.parts
            .filter(
              (part): part is { kind: "text"; text: string; phase?: "answer" | "process" } =>
                part.kind === "text",
            )
            .filter((part) => (part.phase === "process" ? "process" : "answer") === phase)
            .map((part) => part.text)
            .join("\n\n");
        const text = textOf("answer");
        const processText = textOf("process");
        const blocks = message.parts
          .filter((part): part is { kind: "block"; block: Block } => part.kind === "block")
          .map((part) => part.block);
        const tools = message.parts
          .filter((part): part is { kind: "tool"; name: string; ok: boolean; preview: string; id?: string } => part.kind === "tool")
          .map((part, index) => ({
            id: part.id ?? `persisted_${index}`,
            name: part.name,
            argsPreview: "",
            ok: part.ok,
            preview: part.preview,
          }));
        if (text.trim() === "" && processText.trim() === "" && blocks.length === 0) continue;
        turns.push({
          id: message.id,
          role: message.role === "user" ? "user" : "assistant",
          createdAt: message.created_at ?? "",
          text,
          processText,
          blocks,
          tools,
          skills: [],
          status: "done",
          error: null,
          usage: null,
          iterations: null,
          runId: null,
        });
      }
      return { ...state, turns, activeTurnId: null, status: "idle" };
    }

    case "user/append": {
      const turn: Turn = {
        id: action.id,
        role: "user",
        createdAt: action.createdAt,
        text: action.content,
        processText: "",
        blocks: [],
        tools: [],
        skills: [],
        status: "done",
        error: null,
        usage: null,
        iterations: null,
        runId: null,
      };
      return { ...state, turns: [...state.turns, turn] };
    }

    case "run/prepare": {
      // Un run nuevo abre un turno de asistente vacío que el stream irá rellenando.
      const turn: Turn = {
        id: action.messageId || `assistant_${action.runId}`,
        role: "assistant",
        createdAt: action.createdAt,
        text: "",
        processText: "",
        blocks: [],
        tools: [],
        skills: [],
        status: "connecting",
        error: null,
        usage: null,
        iterations: null,
        runId: action.runId,
      };
      return {
        ...state,
        turns: [...state.turns, turn],
        activeTurnId: turn.id,
        activeRunId: action.runId,
        status: "connecting",
      };
    }

    case "run/failed": {
      // El POST falló: se deja constancia como turno del asistente en error
      // para que la conversación no quede muda.
      const turn: Turn = {
        id: localTurnId(),
        role: "assistant",
        createdAt: action.createdAt,
        text: "",
        processText: "",
        blocks: [],
        tools: [],
        skills: [],
        status: "error",
        error: { code: "request_failed", message: action.message, fatal: true },
        usage: null,
        iterations: null,
        runId: null,
      };
      return { ...state, turns: [...state.turns, turn], status: "error" };
    }

    case "run/replay": {
      // Una reconexión reenvía el run desde el principio: se limpia el turno
      // para que volver a aplicarlo sea idempotente.
      const turnId = state.activeTurnId;
      if (!turnId) return state;
      return updateTurn(state, turnId, (turn) => ({
        ...turn,
        text: "",
        processText: "",
        blocks: [],
        tools: [],
        skills: [],
        status: "connecting",
      }));
    }

    case "stream/event": {
      const event = action.event;
      const turnId = state.activeTurnId;
      const now = Date.now();

      switch (event.kind) {
        case "ignore":
          return { ...state, lastEventAt: now };

        case "run.start": {
          if (event.data.run_id && event.data.run_id !== state.activeRunId) {
            return {
              ...state,
              activeRunId: event.data.run_id,
              status: "streaming",
              lastEventAt: now,
            };
          }
          return updateTurn({ ...state, status: "streaming", lastEventAt: now }, turnId, (turn) => ({
            ...turn,
            status: "streaming",
          }));
        }

        case "text.delta": {
          if (!event.data.delta) return state;
          // El backend marca cada fragmento: `process` es lo que el agente dice
          // mientras trabaja, `answer` la conclusión. Van a campos distintos
          // porque la UI los presenta de forma muy distinta. Un evento sin
          // `phase` (backend antiguo) se trata como respuesta.
          const field = event.data.phase === "process" ? "processText" : "text";
          return updateTurn(
            { ...state, status: "streaming", lastEventAt: now },
            turnId,
            (turn) => ({
              ...turn,
              [field]: turn[field] + event.data.delta,
              status: "streaming",
            }),
          );
        }

        case "block.add":
          // Un preset NO abre la vista por su cuenta: llegaría a mitad de una
          // respuesta y le cortaría la lectura al estudiante. Se queda como
          // tarjeta en el hilo, y la vista se abre solo si la persona pulsa el
          // botón (`preset/open`).
          return updateTurn({ ...state, lastEventAt: now }, turnId, (turn) => ({
            ...turn,
            blocks: appendBlock(turn.blocks, event.data.block),
          }));

        case "tool.call":
          return updateTurn({ ...state, lastEventAt: now }, turnId, (turn) => ({
            ...turn,
            tools: [
              ...turn.tools.filter((tool) => tool.id !== event.data.id),
              {
                id: event.data.id,
                name: event.data.name,
                argsPreview: event.data.args_preview,
                ok: null,
                preview: "",
              },
            ],
          }));

        case "tool.result":
          return updateTurn({ ...state, lastEventAt: now }, turnId, (turn) => ({
            ...turn,
            tools: turn.tools.map((tool) =>
              tool.id === event.data.id
                ? { ...tool, ok: event.data.ok, preview: event.data.preview }
                : tool,
            ),
          }));

        case "skill.loaded":
          return updateTurn({ ...state, lastEventAt: now }, turnId, (turn) =>
            turn.skills.includes(event.data.name)
              ? turn
              : { ...turn, skills: [...turn.skills, event.data.name] },
          );

        case "run.done": {
          const usage = event.data.usage;
          const next = updateTurn({ ...state, status: "done", lastEventAt: now }, turnId, (turn) => ({
            ...turn,
            status: "done",
            usage: usage
              ? {
                  inputTokens: usage.input_tokens ?? 0,
                  outputTokens: usage.output_tokens ?? 0,
                }
              : null,
            iterations: typeof event.data.iterations === "number" ? event.data.iterations : null,
          }));
          return { ...next, activeTurnId: null };
        }

        case "error": {
          const next = updateTurn({ ...state, status: "error", lastEventAt: now }, turnId, (turn) => ({
            ...turn,
            status: "error",
            error: { code: event.data.code, message: event.data.message, fatal: true },
          }));
          return { ...next, activeTurnId: null };
        }
      }
      return state;
    }

    case "stream/interrupted": {
      const turnId = state.activeTurnId;
      if (!turnId) return { ...state, status: "error" };
      return updateTurn({ ...state, status: "error" }, turnId, (turn) => ({
        ...turn,
        status: "error",
        error: { code: "stream_interrupted", message: action.message, fatal: false },
      }));
    }

    case "stream/stopped": {
      const turnId = state.activeTurnId;
      const next = updateTurn({ ...state, status: "done" }, turnId, (turn) => ({
        ...turn,
        status: "done",
      }));
      return { ...next, activeTurnId: null };
    }

    case "preset/open":
      return { ...state, preset: action.preset };

    case "preset/close":
      return { ...state, preset: null };

    default:
      return state;
  }
}

/* -------------------------------------------------------------------------- */
/* Selectores                                                                 */
/* -------------------------------------------------------------------------- */

export function isRunActive(state: StreamState): boolean {
  return state.status === "streaming" || state.status === "connecting";
}
